/**
 * Explains why a filter combination returns little or nothing.
 *
 * WHY THIS EXISTS
 * ---------------
 * "Nothing to rank. Try fewer criteria, or a different category." is a shrug. It
 * tells the reader that something is wrong without saying what, so the only way
 * forward is to toggle things at random until results reappear.
 *
 * Most empty results have ONE identifiable cause, and naming it turns a dead end
 * into a decision. The checks below are structural — they follow from what the
 * criteria measure, not from guessing at the data — so they can warn BEFORE a
 * request is even sent.
 */

export type NoticeTone = 'conflict' | 'sparse' | 'info';

export interface CombinationNotice {
  tone: NoticeTone;
  /** One line, stating the cause rather than the symptom. */
  message: string;
  /** The concrete next move, when there is an obvious one. */
  suggestion?: string;
}

/** Criteria that can only be measured on something sold by volume. */
const VOLUME_ONLY = new Set(['cheapest_per_100ml']);

/** Criteria that can only be measured on something sold by weight. */
const MASS_ONLY = new Set(['cheapest_per_100g']);

/** Criteria that need a resolved nutrition label. */
const NEEDS_NUTRITION = new Set([
  'protein_per_dollar',
  'cheapest_per_100kcal',
  'protein_per_100kcal',
  'carbs_per_100kcal',
  'fat_per_100kcal',
  'highest_protein',
  'highest_carbs',
  'highest_fat',
]);

export interface SanityInput {
  selected: string[];
  category: string | null;
  audience: 'human' | 'pet' | 'all';
  /** How many products qualified, once known. */
  eligible: number | null;
  /** Rows actually returned, once known. */
  returned: number | null;
  /** Share of the catalog carrying nutrition, 0–100. */
  coveragePercent: number | null;
}

/**
 * Inspect a combination and return what is worth saying about it.
 *
 * Ordered by how much each explains: a structural conflict is the whole story,
 * whereas thin coverage is a caveat on results that do exist.
 */
export function checkCombination(input: SanityInput): CombinationNotice | null {
  const { selected, category, audience, eligible, returned, coveragePercent } = input;

  // 1. Mass and volume together. A product is sold by weight OR by volume, so no
  //    single product can be measured on both. The ranking still works — each
  //    product is scored on the criteria it qualifies for — but a reader
  //    expecting every row to satisfy everything deserves to know why it cannot.
  const wantsVolume = selected.some((s) => VOLUME_ONLY.has(s));
  const wantsMass = selected.some((s) => MASS_ONLY.has(s));
  if (wantsVolume && wantsMass) {
    /*
     * INFORMATION, NOT A CONFLICT.
     *
     * This was styled as a warning, which invited the obvious question: if the
     * two cannot both apply, why is selecting both allowed? The answer is that
     * the combination is a perfectly good one — the ranking is a UNION, so a
     * pack sold by weight is scored on per-100 g and one sold by volume on
     * per-100 ml, and the board ends up covering both. Selecting both is how you
     * ask for "cheapest per unit, whichever unit it is sold in"; checked live,
     * the top six comes back three by weight and three by volume.
     *
     * Greying one out on selecting the other would remove that. So the fix was
     * not to restrict the choice but to stop describing a working combination in
     * the language of a fault.
     */
    return {
      tone: 'info',
      message:
        'Per 100 g and per 100 ml measure different packs — one is sold by weight, the other by volume.',
      suggestion: 'Each row is scored on whichever applies to it, so the ranking covers both.',
    };
  }

  // 2. Nutrition criteria against a catalog that mostly lacks nutrition. This is
  //    the commonest cause of a thin board and has nothing to do with the
  //    category chosen.
  const needsNutrition = selected.filter((s) => NEEDS_NUTRITION.has(s));
  if (needsNutrition.length > 0 && coveragePercent !== null && coveragePercent < 25) {
    if (eligible !== null && eligible === 0) {
      return {
        tone: 'conflict',
        message: `No product ${category === null ? '' : `in ${category} `}has the nutrition data these criteria need.`,
        suggestion: 'Try a criterion based on price or pack size, which every product has.',
      };
    }
    if (eligible !== null && eligible < 200) {
      return {
        tone: 'sparse',
        message: `Ranking ${eligible.toLocaleString()} products — only ${coveragePercent}% of the catalog has nutrition data yet.`,
        suggestion: 'Positions may change as more labels are collected.',
      };
    }
  }

  // 3. Empty with no nutrition criteria involved: the category or audience is
  //    the constraint, and which one it is can be stated precisely.
  if (returned === 0) {
    if (audience === 'pet' && category !== null) {
      return {
        tone: 'conflict',
        message: `No pet products in ${category}.`,
        suggestion: 'Switch to People, or clear the category.',
      };
    }
    if (category !== null) {
      return {
        tone: 'conflict',
        message: `Nothing in ${category} can be measured by these criteria.`,
        suggestion: 'Clear the category, or pick a criterion that applies more broadly.',
      };
    }
    return {
      tone: 'conflict',
      message: 'No product satisfies every one of these criteria.',
      suggestion: 'Remove one to widen the field.',
    };
  }

  // 4. Results exist but the field is small enough that a "ranking" overstates it.
  if (eligible !== null && eligible > 0 && eligible < 12) {
    return {
      tone: 'sparse',
      message: `Only ${eligible} product${eligible === 1 ? '' : 's'} qualify, so this is a short list rather than a ranking.`,
    };
  }

  return null;
}
